import React from 'react';
import { PaymentViewTemplate } from '../../../../templates/PaymentViewTemplate';
import { EligibilityPlanProps, usePaymentContext } from '../../../../contexts/PaymentContext';
import { PAYMENT_TYPE } from '../../../../utils/constants';
import { Page, useExecutiveCheckupRouter } from '../../router/context';
import { useScheduleContext } from '../../../../contexts/ScheduleContext';
import { ScheduleItemPropsBuilder } from '../../../../models/Schedule';
import { fetchCatalogService } from '../../../../modules/products';
import { useServiceContext } from '../../../../contexts/ServiceContext';
import { LoadingOverlay } from '../../../../components';
import { ServiceItemProps } from '../../../../models/Service';
import { PatientProps, usePatientContext } from '../../../../contexts/PatientContext';
import { fetchValidateExecutiveCheckup } from '../../../../modules/validateRules';
import { makePatientRuleByPatientProps } from '../../../../models/Patient';
import { PAYMENT_TYPES, PaymentValidationRule } from '../../../../models/PaymentRule';
import SystemUnavailable from '../../../../components/SystemUnavailable';
import { UnavailableReturnsModal } from '../../components/UnavailableReturnsModal';
import { ReturnsAlreadyScheduledModal } from '../../components/ReturnsAlreadyScheduledModal';
import { ProductItemProps } from '../../../../models/Product';
import { LateAppointmentError } from '../../../../errors/LateAppointmentError';
import { UnavailableReturnsError } from '../../../../errors/UnavailableReturnsError';
import { FirstAppointmentUnavailableError } from '../../../../errors/FirstAppointmentUnavailableError';
import { SystemUnavailableError } from '../../../../errors';
import ModalNoEligibility from '../../components/ModalNoEligibility';
import { LateAppointmentModal } from '../../components/LateAppointmentModal';

type PaymentListProps = {
  isFirstAppointment?: boolean;
};

const PaymentList = ({ isFirstAppointment }: PaymentListProps) => {
  const { selectedService: service, setSeletedService: setSelectedService } = useServiceContext();
  const { navigate } = useExecutiveCheckupRouter();
  const { eligibilityPlans } = usePaymentContext();
  const { changeSchedule, selectedItem } = useScheduleContext();
  const { patientSelected } = usePatientContext();

  const [loading, setLoading] = React.useState(false);
  const [validatedIsFirstAppointment, setValidatedIsFirstAppointment] = React.useState(false);
  const [scheduleIsNotAllowed, setScheduleIsNotAllowed] = React.useState(false);
  const [isModalUnavailableReturnsVisible, setIsModalUnavailableReturnsVisible] = React.useState(false);
  const [isModalSystemUnavailableVisible, setIsModalSystemUnavailableVisible] = React.useState(false);
  const [isModalReturnsAlreadyScheduledVisible, setIsModalReturnsAlreadyScheduledVisible] = React.useState(false);
  const [isModalNoEligibility, setIsModalNoEligibility] = React.useState(false);
  const [isModalLateAppointmentVisible, setIsModalLateAppointmentVisible] = React.useState(false);

  function createSchedule(props: { product: ProductItemProps; eligibilityPlan?: EligibilityPlanProps }) {
    const scheduleFactory = ScheduleItemPropsBuilder();

    if (props.product?.id) scheduleFactory.setProduct(props.product);

    if (props?.eligibilityPlan)
      scheduleFactory.setPayment({
        type: PAYMENT_TYPE.B2B,
        eligibility: {
          idInsurance: props?.eligibilityPlan.id_convenio,
          idCustomer: props?.eligibilityPlan.id_cliente,
          codInsurance: props?.eligibilityPlan.cod_convenio,
          codPlan: props?.eligibilityPlan.cod_plano,
          codOffice: props?.eligibilityPlan.cod_escritorio,
          plan: props?.eligibilityPlan.nome,
          image: props?.eligibilityPlan.image
        }
      });

    changeSchedule({ ...selectedItem, ...scheduleFactory.build() });
  }

  function makePaymentValidationByEligibilityPlan(eligibilityPlan: EligibilityPlanProps) {
    return {
      type: PAYMENT_TYPES.B2B,
      additional_info: {
        plan_code: eligibilityPlan.cod_plano
      }
    } as PaymentValidationRule;
  }

  function productName(name: string) {
    return (name.split(' - ')[1] || name).trim();
  }

  const handleError = (error: Error) => {
    if (error instanceof LateAppointmentError) setIsModalLateAppointmentVisible(true);
    if (error instanceof UnavailableReturnsError) setIsModalUnavailableReturnsVisible(true);
    if (error instanceof FirstAppointmentUnavailableError) setIsModalNoEligibility(true);
    if (error instanceof SystemUnavailableError) setIsModalSystemUnavailableVisible(true);
  };

  async function validateRulesCheckup(props: {
    service: ServiceItemProps;
    patient: PatientProps;
    eligibilityPlan: EligibilityPlanProps;
  }) {
    const { eligibilityPlan, patient, service: checkup } = props;

    try {
      const response = await fetchValidateExecutiveCheckup({
        service: checkup,
        patient: makePatientRuleByPatientProps(patient),
        payment: makePaymentValidationByEligibilityPlan(eligibilityPlan)
      });

      const { isFirstAppointment, service } = response;

      setValidatedIsFirstAppointment(!!isFirstAppointment);

      if (service?.products?.length === 0) {
        setIsModalReturnsAlreadyScheduledVisible(true);
        setScheduleIsNotAllowed(true);
      } else {
        const createScheduleParams = {
          eligibilityPlan,
          product: {} as ProductItemProps
        };

        if (service?.products.length === 1) {
          const [product] = service.products;

          createScheduleParams.product = product;
        }

        const products = service.products.map((product: ProductItemProps) => {
          return {
            ...product,
            name: productName(product.name)
          };
        });

        setSelectedService({
          ...service,
          products
        });

        createSchedule(createScheduleParams);
      }
    } catch (error: any) {
      console.error('Error trying validate service', error);

      handleError(error);
    }

    setScheduleIsNotAllowed(false);
  }

  async function handlePayment(eligibilityPlan: EligibilityPlanProps) {
    setLoading(true);

    const res = await fetchCatalogService(service.id);

    if (patientSelected) await validateRulesCheckup({ service: res.data, eligibilityPlan, patient: patientSelected });

    setLoading(false);
  }

  const handleContinue = () => navigate(Page.INFO, { isFirstAppointment: validatedIsFirstAppointment });

  return (
    <>
      <PaymentViewTemplate
        eligibilityPlans={eligibilityPlans}
        handleSelectPayment={handlePayment}
        handleSelectedPayment={handleContinue}
        buttonDisable={scheduleIsNotAllowed}
      />
      <LoadingOverlay loading={loading} />

      <SystemUnavailable
        visible={isModalSystemUnavailableVisible}
        onClose={() => setIsModalSystemUnavailableVisible(false)}
      />
      <UnavailableReturnsModal
        visible={isModalUnavailableReturnsVisible}
        onClose={() => setIsModalUnavailableReturnsVisible(false)}
      />
      <ReturnsAlreadyScheduledModal
        visible={isModalReturnsAlreadyScheduledVisible}
        onClose={() => setIsModalReturnsAlreadyScheduledVisible(false)}
      />
      <ModalNoEligibility visible={isModalNoEligibility} onClose={() => setIsModalNoEligibility(false)} />

      <LateAppointmentModal
        visible={isModalLateAppointmentVisible}
        onClose={() => setIsModalLateAppointmentVisible(false)}
      />
    </>
  );
};

export default PaymentList;
